import React, { useEffect, useRef, useState } from "react";
import { fetchAdminSales, fetchSalesSummary } from "../../api/transactions";
import { useNavigate } from "react-router-dom";
import SalesLog from "./SalesLog";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

const RANGE_PRESETS = [
  { value: "today", label: "오늘" },
  { value: "week", label: "이번 주" },
  { value: "month", label: "이번 달" },
  { value: "custom", label: "직접선택" },
];

const DATE_FORMAT = "YYYY-MM-DD";

// 프리셋에 해당하는 KST 날짜 범위 계산 (주 시작은 dayjs 기본 = 일요일)
const getPresetRange = (preset) => {
  const now = dayjs().tz("Asia/Seoul");
  const to = now.format(DATE_FORMAT);
  if (preset === "today") return { from: to, to };
  if (preset === "week") return { from: now.startOf("week").format(DATE_FORMAT), to };
  return { from: now.startOf("month").format(DATE_FORMAT), to };
};

const SalesManagement = ({ admin }) => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState(null);
  // 한 번에 받는 건수를 넘겼을 때만 [더 보기]가 생긴다(프리셋은 전부 상한 안쪽이라 안 생긴다)
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // 집계 데이터 상태
  const [salesByPayment, setSalesByPayment] = useState({});
  const [salesByDate, setSalesByDate] = useState({});
  const [topSellingProducts, setTopSellingProducts] = useState([]);

  // 상세 결제 내역 기간 필터 (기본: 이번 달)
  const [rangePreset, setRangePreset] = useState("month");
  const [customFrom, setCustomFrom] = useState(() => getPresetRange("month").from);
  const [customTo, setCustomTo] = useState(() => getPresetRange("month").to);

  // 화면에 한 번에 쌓아둘 최대 행수. 이어붙이기를 무제한 허용하면 DOM이 다시 폭주해
  // (실측: 행당 약 21노드) 이번 수정의 목적이 무너진다. 여기 닿으면 기간을 좁히도록 안내한다.
  const MAX_RENDERED_ROWS = 6000;

  // 지금 화면이 보고 있는 기간의 서명. 늦게 도착한 응답을 버리는 기준으로 쓴다.
  const rangeKey =
    rangePreset === "custom" ? `custom|${customFrom}|${customTo}` : rangePreset;
  const rangeKeyRef = useRef(rangeKey);

  const navigate = useNavigate();

  useEffect(() => {
    // console.log("📌 현재 sales 상태:", sales);
  }, [sales]);

  // 집계 카드: 화면 진입 시 1회만 조회
  useEffect(() => {
    if (admin === undefined) return; // ✅ undefined 상태일 때는 아무 동작도 하지 않음
  if (!admin) {
      console.warn("❌ 관리자 정보 없음, 로그인 페이지로 이동");
      navigate("/login");
      return;
    }

    const loadSummary = async () => {
      setLoading(true);
      setError(null); // ✅ 기존 오류 초기화

      if (!admin?.id) {
        console.error("❌ 관리자 ID가 없습니다.");
        setError("매출 데이터를 불러오는 중 오류가 발생했습니다.");
        setLoading(false);
        return;
      }

      const summary = await fetchSalesSummary(admin.id);
      if (!summary) {
        setError("매출 데이터를 불러오는 중 오류가 발생했습니다.");
      } else {
        applySummary(summary);
      }
      setLoading(false);
    };

    loadSummary();
  }, [admin, navigate]);

  // 상세 결제 내역: 기간이 바뀔 때마다 조회
  useEffect(() => {
    if (!admin?.id) return;

    const range =
      rangePreset === "custom"
        ? { from: customFrom, to: customTo }
        : getPresetRange(rangePreset);

    if (!range.from || !range.to) return;
    if (range.from > range.to) return; // 직접선택에서 시작일 > 종료일이면 조회하지 않음

    // 늦게 도착한 이전 기간의 응답이 화면을 덮지 않도록, 요청 시점의 기간 서명을 남긴다.
    // [더 보기]도 같은 서명을 보고 판단하므로 두 경로가 서로를 오염시키지 않는다.
    rangeKeyRef.current = rangeKey;
    let cancelled = false;
    const loadSales = async () => {
      setListLoading(true);
      setLoadFailed(false);
      const { rows, hasMore: more, failed } = await fetchAdminSales(admin.id, range);
      if (cancelled || rangeKeyRef.current !== rangeKey) return;

      if (failed) {
        console.warn("⚠️ 매출 내역을 불러오지 못했습니다.");
        setLoadFailed(true);
      }
      setSales(rows);
      setHasMore(more);
      setListLoading(false);
    };

    loadSales();
    return () => { cancelled = true; };
  }, [admin, rangePreset, customFrom, customTo, rangeKey]);

  // [더 보기] — 이미 받은 건수를 offset으로 삼아 다음 묶음을 이어붙인다.
  // 그 사이 새 결제가 들어오면 경계 항목이 겹칠 수 있어 id로 중복을 걸러낸다.
  const loadMoreSales = async () => {
    if (!admin?.id || loadingMore) return;
    setLoadingMore(true);
    setLoadFailed(false);
    const requestedKey = rangeKeyRef.current;
    const range =
      rangePreset === "custom"
        ? { from: customFrom, to: customTo }
        : getPresetRange(rangePreset);

    const { rows, hasMore: more, failed } = await fetchAdminSales(admin.id, range, sales.length);

    // 응답을 기다리는 사이 기간이 바뀌었으면 이 결과는 다른 기간의 것이라 버린다
    if (rangeKeyRef.current !== requestedKey) {
      setLoadingMore(false);
      return;
    }
    if (failed) {
      // 실패를 "더 없음"으로 삼키면 잘린 목록이 전체처럼 보인다. hasMore는 그대로 둔다.
      setLoadFailed(true);
      setLoadingMore(false);
      return;
    }
    setSales((prev) => {
      const seen = new Set(prev.map((s) => s.id));
      return [...prev, ...rows.filter((s) => !seen.has(s.id))];
    });
    setHasMore(more);
    setLoadingMore(false);
  };

  // summary API 응답을 기존 화면 표기 형태로 변환 (합계는 문자열로 올 수 있음)
  const applySummary = (summary) => {
    const byPayment = {};
    (summary.by_payment || []).forEach((row) => {
      byPayment[row.payment_method || "기타"] = parseFloat(row.total) || 0;
    });
    setSalesByPayment(byPayment);

    const period = summary.by_period || {};
    setSalesByDate({
      오늘: parseFloat(period.today) || 0,
      "이번 주": parseFloat(period.week) || 0,
      "이번 달": parseFloat(period.month) || 0,
      "이번 년도": parseFloat(period.year) || 0,
    });

    setTopSellingProducts(
      (summary.top_products || []).map((prod) => ({
        name: prod.name,
        quantity: parseFloat(prod.quantity) || 0,
      }))
    );
  };

  // UTC → KST 변환 함수
  const convertToKST = (utcDate) => {
    if (!utcDate) return "시간 정보 없음";
    return new Date(utcDate).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" });
  };

  return (
    <div className="p-6">
      <div className="font-600 text-2xl">매출 관리</div>
      {loading ? (
        <p>로딩 중...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <div className="flex flex-col md:flex-row">
          {/* 왼쪽 컬럼: 집계 정보 */}
          <div className="md:w-2/3 md:pr-4">
            {/* 결제수단별 매출 정보 */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
              <h2 className="text-2xl font-bold mb-2">결제수단별 매출</h2>
              {Object.entries(salesByPayment).map(([method, total]) => (
                <p key={method}>
                  {method}: {Math.floor(total).toLocaleString()}원
                </p>
              ))}
            </div>

            {/* 날짜별 매출 정보 */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
              <h2 className="text-2xl font-bold mb-2">날짜별 매출</h2>
              {Object.entries(salesByDate).map(([period, total]) => (
                <p key={period}>
                  {period}: {Math.floor(total).toLocaleString()}원
                </p>
              ))}
            </div>

            {/* 판매량 높은 상품 랭킹 */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
              <h2 className="text-2xl font-bold mb-2">판매량 높은 상품 랭킹</h2>
              {topSellingProducts.map((prod, idx) => (
                <p key={idx}>
                  {idx + 1}. {prod.name}: {prod.quantity}개
                </p>
              ))}
            </div>
          </div>

          {/* 오른쪽 컬럼: 상세 결제 내역 (SalesLog) */}
          <div className="md:w-1/3">
            <div className="bg-white p-4 rounded-lg shadow">
              <h2 className="text-2xl font-bold mb-2">상세 결제 내역</h2>

              {/* 기간 프리셋 */}
              <div className="flex flex-wrap gap-2 mb-3">
                {RANGE_PRESETS.map(({ value, label }) => (
                  <button
                    key={value}
                    className={`px-2.5 py-1 rounded-md text-sm ${
                      rangePreset === value
                        ? "bg-gray-950 text-white font-600"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }`}
                    onClick={() => setRangePreset(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* 직접선택 날짜 입력 */}
              {rangePreset === "custom" && (
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="date"
                    value={customFrom}
                    max={customTo}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="border border-gray-200 rounded-md px-2 py-1 text-sm"
                  />
                  <span className="text-gray-400">~</span>
                  <input
                    type="date"
                    value={customTo}
                    min={customFrom}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="border border-gray-200 rounded-md px-2 py-1 text-sm"
                  />
                </div>
              )}

              {listLoading ? <p>로딩 중...</p> : <SalesLog sales={sales} />}

              {/* 기간이 아주 넓어 한 번에 다 못 받은 경우에만 나타난다 */}
              {!listLoading && hasMore && (
                <div className="flex flex-col items-center gap-1 py-4">
                  {sales.length >= MAX_RENDERED_ROWS ? (
                    <span className="text-sm text-gray-700">
                      {sales.length.toLocaleString()}건까지 표시했습니다. 더 보시려면 기간을 좁혀 주세요.
                    </span>
                  ) : (
                    <>
                      <button
                        onClick={loadMoreSales}
                        disabled={loadingMore}
                        className="px-5 py-2 text-sm border border-gray-900 text-gray-900 rounded-2xl hover:bg-gray-900 hover:text-white disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200"
                      >
                        {loadingMore ? "불러오는 중..." : "더 보기"}
                      </button>
                      <span className="text-xs text-gray-500">
                        {sales.length.toLocaleString()}건까지 표시했습니다
                      </span>
                    </>
                  )}
                  {loadFailed && (
                    <span className="text-xs text-red-500">
                      불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
                    </span>
                  )}
                </div>
              )}

              {/* 목록 자체를 못 받은 경우 — 빈 목록이 "매출 없음"으로 오해되지 않게 알린다 */}
              {!listLoading && loadFailed && sales.length === 0 && (
                <p className="py-4 text-sm text-red-500">
                  매출 내역을 불러오지 못했습니다. 기간을 다시 선택해 주세요.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesManagement;
